import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { GeoReportStatusResponse } from '../../../models/geo-report/GeoReportResponse';
import { getGeoReportStatus } from '../../../services/geo-report/GeoReportService';
import { useAccount } from '../../../contexts/account/AccountContexts';
import { scheduleIntercomChecklistRefresh } from '../../../services/intercom/trackEvent';
import { GET_GEO_REPORT_STATUS, GET_GEO_REPORTS } from '../../query/geo-report/keys';

interface UseGeoReportStatusPollParams {
  id: number | null;
  status: 'PENDING' | 'GENERATING' | 'COMPLETED' | 'FAILED' | null;
}

export const useGeoReportStatusPoll = ({ id, status }: UseGeoReportStatusPollParams) => {
  const queryClient = useQueryClient();
  const { accountResponse } = useAccount();
  const refreshedReportIdRef = useRef<number | null>(null);
  const isPolling = status === 'PENDING' || status === 'GENERATING';

  const { data } = useQuery<GeoReportStatusResponse>({
    queryKey: [GET_GEO_REPORT_STATUS, id],
    queryFn: () => getGeoReportStatus(id!),
    enabled: !!id && isPolling,
    refetchInterval: isPolling ? 5000 : false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (data?.status === 'COMPLETED' || data?.status === 'FAILED') {
      queryClient.invalidateQueries({ queryKey: [GET_GEO_REPORTS] });
    }
  }, [data?.status, queryClient]);

  useEffect(() => {
    if (data?.status === 'COMPLETED' && refreshedReportIdRef.current !== id) {
      refreshedReportIdRef.current = id;
      scheduleIntercomChecklistRefresh(accountResponse);
    }
  }, [accountResponse, data?.status, id]);

  return { data };
};
